import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import EntitlementBlockerModal from "../components/EntitlementBlockerModal";
import {
  buildEntitlementModalModel,
  extractEntitlementModalModelFromError,
} from "../constants/entitlementBlockers";
import { useEntitlements } from "./EntitlementsContext";

const EntitlementBlockerModalContext = createContext(null);

export const useEntitlementBlockerModal = () => {
  const context = useContext(EntitlementBlockerModalContext);
  if (!context) {
    throw new Error(
      "useEntitlementBlockerModal must be used within an EntitlementBlockerModalProvider",
    );
  }
  return context;
};

export const EntitlementBlockerModalProvider = ({ children }) => {
  const { planCode } = useEntitlements();
  const [isOpen, setIsOpen] = useState(false);
  const [modalModel, setModalModel] = useState(null);

  const closeBlockerModal = useCallback(() => {
    setIsOpen(false);
  }, []);

  const openBlockerModal = useCallback(
    (payload = {}) => {
      const model = buildEntitlementModalModel({
        ...payload,
        planCode: payload?.planCode || planCode,
      });

      if (!model) return false;

      setModalModel(model);
      setIsOpen(true);
      return true;
    },
    [planCode],
  );

  const showEntitlementBlocker = useCallback(
    (error, { context = {} } = {}) => {
      if (!error) return false;
      if (error.__entitlementBlockerHandled === true) return true;

      const model = extractEntitlementModalModelFromError(error, {
        fallbackPlanCode: planCode,
        context,
      });

      if (!model) return false;

      try {
        // Prevent duplicate modal invocations from both global interceptor and local catch blocks.
        // eslint-disable-next-line no-param-reassign
        error.__entitlementBlockerHandled = true;
      } catch {
        // Ignore non-extensible error objects.
      }

      setModalModel(model);
      setIsOpen(true);
      return true;
    },
    [planCode],
  );

  const value = useMemo(
    () => ({
      isOpen,
      modalModel,
      openBlockerModal,
      closeBlockerModal,
      showEntitlementBlocker,
    }),
    [
      isOpen,
      modalModel,
      openBlockerModal,
      closeBlockerModal,
      showEntitlementBlocker,
    ],
  );

  return (
    <EntitlementBlockerModalContext.Provider value={value}>
      {children}
      <EntitlementBlockerModal
        open={isOpen}
        model={modalModel}
        onClose={closeBlockerModal}
      />
    </EntitlementBlockerModalContext.Provider>
  );
};
